import { createFileRoute, Link } from "@tanstack/react-router";
import { DealLink, DealDate } from "@/components/deal-link";
import { TravelpayoutsWidget } from "@/components/travelpayouts-widget";
import { SearchClickStats } from "@/components/search-click-stats";
import { PopularAirports } from "@/components/popular-airports";
import { HomeFlightGuide } from "@/components/home-flight-guide";
import { Header, Footer } from "@/components/layout";
import { PopularRoutesSkeleton } from "@/components/loading-skeletons";
import { buildMeta } from "@/lib/seo";
import { HOME_FAQ, homeFaqJsonLd } from "@/lib/home-faq";
import { getPopularRoutes, type PopularRoute } from "@/lib/popular-routes.functions";
import { getInitialOrigin } from "@/lib/initial-origin.functions";
import { getDealsFromOrigin, type Deal } from "@/lib/deals-from-origin.functions";
import { getBiggestDrops, type PriceDrop } from "@/lib/biggest-drops.functions";
import { findAirport } from "@/lib/airports";
import { PriceBadge } from "@/components/price-badge";
import heroImg from "@/assets/hero-flight.webp";
import { Sparkles, Shield, Zap, TrendingDown, Flame, ArrowDownRight } from "lucide-react";

export const Route = createFileRoute("/")({
  loader: async () => {
    const [popular, origin] = await Promise.all([
      getPopularRoutes(),
      getInitialOrigin({ data: { page: "/" } }).catch(() => ({
        from: "WAR" as const,
        reason: "default" as const,
      })),
    ]);
    // Deal feed z miasta usera (geo-IP). Liczone osobno, żeby nie blokować
    // głównego loadera jeśli route_prices jest puste dla danego miasta.
    const [deals, drops] = await Promise.all([
      getDealsFromOrigin({ data: { from: origin.from, limit: 12 } }).catch(() => [] as Deal[]),
      getBiggestDrops({ data: { limit: 8 } }).catch(() => [] as PriceDrop[]),
    ]);
    return { popular, initialFrom: origin.from, deals, drops };
  },
  head: () => {
    const base = buildMeta({
      // ≤60 znaków, żeby Google nie ucinał tytułu w SERP-ach.
      title: "Tanie latanie – wyszukiwarka tanich lotów | TanieLatanie.pl",

      description:
        "Znajdź tanie loty z Polski. Porównaj połączenia w wyszukiwarce Aviasales, sprawdź daty, bagaż i warunki oferty przed rezerwacją u sprzedawcy.",
      path: "/",
    });
    return {
      ...base,
      links: [
        ...(base.links ?? []),
        { rel: "preload", as: "image", href: heroImg, fetchPriority: "high" },
      ],
      scripts: [{ type: "application/ld+json", children: homeFaqJsonLd() }],
    };
  },
  // Skeleton podczas ładowania loader-a (nawigacja klientowa, wolne łącza).
  pendingMs: 200,
  pendingComponent: () => (
    <div className="min-h-screen">
      <Header />
      <section className="container mx-auto px-4 py-10">
        <div className="mb-6">
          <div className="h-8 w-64 animate-pulse rounded bg-muted/30" />
          <div className="mt-2 h-4 w-80 animate-pulse rounded bg-muted/20" />
        </div>
        <PopularRoutesSkeleton count={12} />
      </section>
      <Footer />
    </div>
  ),
  component: Home,
});

function Home() {
  const { popular, initialFrom, deals, drops } = Route.useLoaderData();
  const originCity = findAirport(initialFrom)?.city ?? initialFrom;
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative z-20">
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <img
            src={heroImg}
            alt="Samolot pasażerski nad chmurami w blasku zachodu słońca"
            width={1920}
            height={1280}
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-x-0 top-0 h-[140%] w-full object-cover opacity-60 [mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/20 to-background" />
        </div>

        <div className="container mx-auto px-2 pb-5 pt-4 md:px-4 md:pt-8">
          <h1 className="font-display text-2xl font-bold leading-[1.1] tracking-tight md:text-5xl md:leading-[1] mx-auto max-w-3xl text-center">
            Tanie latanie –{" "}
            <span className="text-gradient-hero-title">wyszukiwarka tanich lotów</span>
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-foreground/90 md:text-base">Porównaj loty z Polski. Wybierz daty, kierunek i ofertę dopasowaną do Twojej podróży.</p>

          <div className="relative z-30 mt-5 w-full">
            <TravelpayoutsWidget />
            <SearchClickStats />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="container relative z-0 mx-auto px-4 py-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { Icon: TrendingDown, t: "Porównaj ceny", d: "Oferty dostępne w Aviasales" },
            { Icon: Zap, t: "Wybierz termin", d: "Loty na konkretne daty" },
            { Icon: Shield, t: "Rezerwacja u sprzedawcy", d: "Warunki sprawdzisz przed zakupem" },
            { Icon: Sparkles, t: "Sprawdź taryfę", d: "Bagaż i dodatki zależą od oferty" },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="glass rounded-lg p-4">
              <Icon className="mb-2 h-5 w-5 text-primary" />
              <div className="font-semibold">{t}</div>
              <div className="text-sm text-muted-foreground">{d}</div>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-8 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium uppercase tracking-widest">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Wyszukiwarka Aviasales · Tanie Latanie
          </div>
        </div>
      </section>

      {/* Deal feed — najtańsze loty z miasta usera */}
      {deals.length > 0 && (
        <section className="container mx-auto px-4 pb-10 pt-5">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold md:text-4xl">
                <span className="inline-flex items-center gap-2">
                  <Flame className="h-7 w-7 text-primary" aria-hidden="true" />
                  Okazje z {originCity}
                </span>
              </h2>
              <p className="text-muted-foreground">
                Ceny zapisane w Aviasales — potwierdź dostępność dla daty wylotu
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {deals
              .slice(
                0,
                deals.length >= 12
                  ? 12
                  : deals.length >= 8
                    ? 8
                    : deals.length >= 6
                      ? 6
                      : deals.length >= 4
                        ? 4
                        : deals.length >= 2
                          ? 2
                          : deals.length,
              )
              .map((d: Deal) => (
                <DealLink
                  key={d.from + d.to}
                  from={d.from}
                  to={d.to}
                  departDate={d.departDate}
                  className="glass group flex items-center justify-between rounded-lg p-4 transition-all hover:shadow-glow"
                  title={`Tanie loty ${d.from} → ${d.to} (${d.city})`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {d.from} → {d.to}
                    </div>
                    <div className="font-display text-lg font-bold truncate group-hover:text-primary">
                      {d.city}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{d.country}</div>
                    {d.badge && (
                      <div className="mt-1.5">
                        <PriceBadge badge={d.badge} size="xs" />
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0 pl-3">
                    <div className="text-[10px] uppercase text-muted-foreground">od</div>
                    <div className="font-semibold text-gradient-sunset whitespace-nowrap">
                      {d.price}
                    </div>
                    <DealDate departDate={d.departDate} />
                  </div>
                </DealLink>
              ))}
          </div>
        </section>
      )}

      {/* Spadki cen — globalna lista największych obniżek vs avg30d */}
      {drops.length > 0 && (
        <section className="container mx-auto px-4 py-10">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold md:text-4xl">
                <span className="inline-flex items-center gap-2">
                  <ArrowDownRight className="h-7 w-7 text-primary" aria-hidden="true" />
                  Największe spadki cen
                </span>
              </h2>
              <p className="text-muted-foreground">
                Trasy, na których ceny spadły najmocniej w ostatnich 30 dniach
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {drops.map((d: PriceDrop) => (
              <DealLink
                key={d.from + d.to}
                from={d.from}
                to={d.to}
                departDate={d.departDate}
                className="glass group relative flex items-center justify-between rounded-lg p-4 transition-all hover:shadow-glow"
                title={`Tanie loty ${d.fromCity} → ${d.toCity}, spadek o ${Math.round(d.dropPct * 100)}%`}
              >
                <div className="min-w-0">
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {d.from} → {d.to}
                  </div>
                  <div className="font-display text-lg font-bold truncate group-hover:text-primary">
                    {d.fromCity} → {d.toCity}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{d.toCountry}</div>
                  <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                    <ArrowDownRight className="h-3 w-3" aria-hidden="true" />−
                    {Math.round(d.dropPct * 100)}% vs śr. 30 dni
                  </div>
                </div>
                <div className="text-right shrink-0 pl-3">
                  <div className="text-[10px] uppercase text-muted-foreground line-through">
                    {d.avg30d} zł
                  </div>
                  <div className="font-semibold text-gradient-sunset whitespace-nowrap">
                    {d.price}
                  </div>
                  <DealDate departDate={d.departDate} />
                </div>
              </DealLink>
            ))}
          </div>
        </section>
      )}

      {/* Popular routes */}
      <section className="container mx-auto px-4 py-10">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-display text-3xl font-bold md:text-4xl">Popularne kierunki</h2>
            <p className="text-muted-foreground">Najczęściej wyszukiwane przez Polaków</p>
          </div>
          <Link
            to="/destinations"
            className="hidden text-sm text-primary hover:underline md:inline"
          >
            Zobacz wszystkie →
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {popular.map((p: PopularRoute) => (
            <Link
              key={p.from + p.to}
              to="/loty/$from/$to"
              params={{ from: p.from.toLowerCase(), to: p.to.toLowerCase() }}
              className="glass group flex items-center justify-between rounded-lg p-5 transition-all hover:shadow-glow"
              title={`Tanie loty ${p.from} → ${p.to} (${p.city})`}
            >
              <div>
                <div className="font-mono text-xs text-muted-foreground">
                  {p.from} → {p.to}
                </div>
                <div className="font-display text-xl font-bold group-hover:text-primary">
                  {p.city}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase text-muted-foreground">Bilety</div>
                <div className="font-semibold text-gradient-sunset">{p.price}</div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <Link
            to="/destinations"
            preload="intent"
            className="glass inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-foreground transition-all hover:shadow-glow hover:text-primary"
            title="Zobacz pełną listę kierunków lotów"
          >
            Zobacz więcej kierunków
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      <PopularAirports />
      <HomeFlightGuide />

      <section className="container mx-auto max-w-3xl px-4 py-14">
        <h2 className="mb-6 font-display text-3xl font-bold md:text-4xl">
          Najczęściej zadawane pytania
        </h2>
        <div className="space-y-3">
          {HOME_FAQ.map((f, i) => (
            <details key={i} className="glass rounded-lg p-5">
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
